import { Alert, Box, Divider, Grid, Link, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { CheckoutState, SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { AddOnItemName, UpgradeDialogSource } from "schemas/dashboard";

import StyledPage from "components/Styled/Page";
import { PAGE_VISITED } from "constants/trackingProps";
import Subscription from "features/Home/UpgradeDialog/Subscription";
import { getParams, trackEvent } from "utils/tracking";

import AddOnCustomEmail from "./AddOns/AddOnsCustomEmail";
import AddOnsEmailVolume from "./AddOns/AddOnsEmailVolume";
import AddOnPartnershipData from "./AddOns/AddOnsPartnershipData";
import AddOnTemplate from "./AddOns/AddOnsTemplates";
import AffiliateProgram from "./AffiliateProgram";
import DedicatedSupport from "./DedicatedSupport";
import GoogleConnect from "./GoogleConnect";
import Referral from "./Referral";
import SubscriptionButton from "./SubscriptionButton";
import UserNotificationsSettings from "./UserNotifications";
import styles from "./styles";

export default function Settings() {
  const { userIntegration } = useContext(UserIntegrationsContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    setCustomDomainDialog,

    setUpgradeDialogSource,
    setOpenCheckoutDialog,
  } = useContext(SubscriptionContext);

  const scrollToSubscription = searchParams.get("section") === "subscription";
  const scrollToAddOn = searchParams.get("section") === "addon";
  const openEmailDialog = searchParams.get("custom-email") === "true";
  const openEmailVolume = searchParams.get("open") === "email_volume";
  const openTemplate = searchParams.get("ref") === "one_time_template_coupon";

  const subscriptionRef = useRef<HTMLDivElement>(null);
  const addOnRef = useRef<HTMLDivElement>(null);

  const openEmailVolumeCheckout = () => {
    setUpgradeDialogSource(UpgradeDialogSource.email_volume_from_ps_email);
    const state: CheckoutState = {
      plan: AddOnItemName.email_volume_yearly,
      source: "Email Volume from P.S Email",
      pageSource: window.location.pathname,
    };
    setOpenCheckoutDialog(state);
    searchParams.delete("email_volume");
    setSearchParams(searchParams);
  };

  const openTemplateCheckout = () => {
    setUpgradeDialogSource(UpgradeDialogSource.free_template_coupon_email);
    const state: CheckoutState = {
      plan: AddOnItemName.pitching_templates,
      source: "Free Template Coupon Email",
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    setOpenCheckoutDialog(state);
    searchParams.delete("ref");
    setSearchParams(searchParams);
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollToSubscription && subscriptionRef.current) {
      subscriptionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [scrollToSubscription]);

  useEffect(() => {
    if ((openEmailDialog || scrollToAddOn) && addOnRef.current) {
      addOnRef.current.scrollIntoView({ behavior: "smooth" });
    }
    if (currentOrg?.id && openEmailDialog) {
      setCustomDomainDialog(true);
    } else if (currentOrg?.id && openEmailVolume) {
      openEmailVolumeCheckout();
    } else if (currentOrg?.id && openTemplate) {
      openTemplateCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentOrg?.id,
    scrollToAddOn,
    openEmailDialog,
    openEmailVolume,
    openTemplate,
  ]);

  return (
    <StyledPage
      content={
        <Grid sx={{ py: 3 }}>
          {!currentOrg?.isBrand && (
            <>
              <DedicatedSupport />

              <Grid
                container
                justifyContent={"space-between"}
                ref={subscriptionRef}
                sx={{ alignItems: "center", gap: 1, mb: 2 }}
              >
                <Typography variant="h2" sx={{ fontSize: { xs: 20, md: 40 } }}>
                  Pick Your Subscription Plan
                </Typography>{" "}
                <SubscriptionButton />
              </Grid>

              <Subscription upgradeDialogSource={null} />

              <Typography ref={addOnRef} variant="h4" gutterBottom>
                Add-Ons
              </Typography>

              <Grid container gap={1} sx={styles.addOnItemGridContainer}>
                {currentOrg?.isVaUser ? (
                  <AddOnsEmailVolume />
                ) : (
                  <AddOnPartnershipData />
                )}

                <AddOnCustomEmail />

                <AddOnTemplate />
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Referral />

              <Divider sx={{ my: 4 }} />

              <AffiliateProgram />

              <Divider sx={{ my: 4 }} />
            </>
          )}

          <Typography variant="h4" gutterBottom>
            Integrations
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs>
              <Typography variant="h6" gutterBottom>
                Google
              </Typography>
              {userIntegration?.email ? (
                <Alert severity="success" sx={{ maxWidth: "600px", my: 1 }}>
                  Your Google account is currently connected. Emails will be
                  sent from <strong>{userIntegration.email}.</strong>
                  <br />
                  <br />
                  If you sign in with another Google account, outstanding
                  follow-up emails will be cancelled, and all scheduled emails
                  will be sent from the newly connected account. Contacts
                  emailed from your old address will no longer be updated.{" "}
                  <strong>This cannot be undone.</strong>
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ maxWidth: "600px", my: 1 }}>
                  No Google account is currently connected.
                </Alert>
              )}

              <Typography variant="body2">
                Connect your Gmail to Bento
              </Typography>
              <Typography variant="body2">
                You can revoke your access at any time.{" "}
                <Link
                  href="https://support.google.com/accounts/answer/3466521?hl=en"
                  target="_blank"
                >
                  Manage your Google access.
                </Link>
              </Typography>
            </Grid>
            <Grid item xs="auto">
              <GoogleConnect />
            </Grid>
          </Grid>

          <Divider sx={{ my: 4 }} />

          <Typography variant="h4" gutterBottom>
            Notifications
          </Typography>

          <Grid item xs={12}>
            <UserNotificationsSettings />
          </Grid>

          <Box sx={{ height: 50 }} />
        </Grid>
      }
    />
  );
}
